import { type MouseEvent, type SVGProps, useCallback, useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Group as GroupIcon } from '@factorialco/f0-react/icons/app'
import { NodeProps } from '@xyflow/react'

import i18n from 'modules/core/lib/i18n'

import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import { useHoverState } from 'modules/workflows/hooks/useHoverState'

import { BasicNodeTheme, getStyle } from './styles'
import { type NodeUI, isNodePropsMultiAction } from './types'

const getMultiActionTheme = (typeTitle: string): BasicNodeTheme => ({
  type: {
    icon: GroupIcon,
    background: '#FDEBD0',
    title: typeTitle,
    color: '#E67E22',
    selectedColor: '#FFF',
    selectedBackground: '#E67E22',
  },
  container: {
    borderColor: '#FDEBD0',
    selectedBorderColor: '#E67E22',
    hoverBorderColor: '#F5D5A8',
  },
})

export const FlowNodeMultiAction = (props: NodeProps<NodeUI>) => {
  const { buildingBlocks, setNodesUI, setMultiActionEditingTaskId, setMultiActionDraft } =
    useWorkflowContext()
  const containerHover = useHoverState(false)

  const multiActionTheme = useMemo(
    () => getMultiActionTheme(i18n.t('workflows.node_type.multi_action.type')),
    []
  )

  const blocksForEmptyState = useMemo(
    () => buildingBlocks.filter((block) => block.key !== 'condition'),
    [buildingBlocks]
  )

  /** From empty state: open sidepanel with Task/Approval form (same as "New action" in MultiActionNodeSidepanel). */
  const handleBlockClick = useCallback(
    (nodeType: string, nodeId: string) => {
      if (nodeType === 'task' || nodeType === 'approvals') {
        setNodesUI((prev) => prev.map((n) => ({ ...n, selected: n.id === nodeId })))
        setMultiActionDraft({ nodeId, type: nodeType })
      }
    },
    [setNodesUI, setMultiActionDraft]
  )

  /** Open sidepanel in edit mode for the clicked task  on the node listed tasks*/
  const handleTaskClick = useCallback(
    (e: PointerEvent | MouseEvent, taskId: string, nodeId: string) => {
      e.stopPropagation()
      setNodesUI((prev) => prev.map((n) => ({ ...n, selected: n.id === nodeId })))
      setMultiActionEditingTaskId(taskId)
    },
    [setNodesUI, setMultiActionEditingTaskId]
  )

  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null)

  if (!isNodePropsMultiAction(props)) return null

  const { data, id: nodeId } = props
  const { typeStyle, typeIconStyle, typeTitleStyle, containerStyle } = getStyle(
    props.selected ?? false,
    data.isValid,
    multiActionTheme,
    containerHover.isHovered
  )
  const TypeIcon = multiActionTheme.type.icon
  const hasTasks = data.parallelTasks.length > 0

  return (
    <Box flexDirection="column" gap="s4">
      <Box
        paddingY="s2"
        width="fit-content"
        borderRadius={{ all: 'abs008' }}
        paddingLeft="s2"
        paddingRight="s4"
        flexDirection="row"
        alignItems="center"
        gap="s2"
        style={typeStyle}
      >
        <TypeIcon width={18} height={18} style={{ ...typeIconStyle }} />
        <Box style={{ color: typeTitleStyle.color }}>{multiActionTheme.type.title}</Box>
      </Box>
      <Box
        width="s360"
        flexDirection="column"
        alignItems="flexStart"
        justifyContent="center"
        borderRadius={{ all: 'abs012' }}
        background="white"
        border={{ all: { style: 'solid', color: 'grey400', width: 's2' } }}
        style={containerStyle}
        {...containerHover.hoverProps}
      >
        <Box
          flexDirection="column"
          alignItems="flexStart"
          justifyContent="center"
          width="full"
          gap="s8"
          padding="s12"
        >
          <Box flexDirection="column" width="full" gap="s4">
            <Text size="300" weight="medium" color="textPrimary">
              {data.title}
            </Text>
            <Text size="100" color="grey700">
              {data.description.length > 100
                ? `${data.description.slice(0, 100)}...`
                : data.description}
            </Text>
          </Box>

          <Box
            width="full"
            style={{ height: '1px', backgroundColor: 'var(--f1-border-secondary, #E2E2E5)' }}
          />

          {!hasTasks ? (
            <Box flexDirection="column" width="full" style={{ gap: '10px' }}>
              {blocksForEmptyState.map((block) => {
                const BlockIcon = block.icon
                const IconWithColor = (iconProps: SVGProps<SVGSVGElement>) => (
                  <BlockIcon
                    {...iconProps}
                    style={{
                      ...(iconProps.style &&
                      typeof iconProps.style === 'object' &&
                      !Array.isArray(iconProps.style)
                        ? iconProps.style
                        : {}),
                      color: block.color,
                    }}
                  />
                )
                return (
                  <F0Button
                    key={block.key}
                    variant="outline"
                    size="sm"
                    icon={IconWithColor}
                    label={block.title}
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation()
                      handleBlockClick(block.nodeType, nodeId)
                    }}
                    style={{ justifyContent: 'flex-start' }}
                  />
                )
              })}
            </Box>
          ) : (
            <Box flexDirection="column" width="full" style={{ gap: '10px' }}>
              {data.parallelTasks.map((task) => {
                const block = blocksForEmptyState.find((b) => b.nodeType === task.type)
                const TaskOrApprovalIcon = block?.icon ?? GroupIcon
                const iconColor = block?.color ?? '#6B7280'
                const isHoveredRow = hoveredTaskId === task.id
                return (
                  <Box
                    key={task.id}
                    flexDirection="row"
                    width="full"
                    padding="s8"
                    borderRadius={{ all: 'abs008' }}
                    gap="s8"
                    alignItems="flexStart"
                    style={{
                      backgroundColor: isHoveredRow ? '#F7F7F7' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => setHoveredTaskId(task.id)}
                    onMouseLeave={() => setHoveredTaskId(null)}
                    onClick={(e) => handleTaskClick(e, task.id, nodeId)}
                  >
                    <TaskOrApprovalIcon
                      width="20"
                      height="20"
                      style={{ color: iconColor, strokeWidth: '1', flexShrink: '0' }}
                    />
                    <Box flexDirection="column" width="full" gap="s2" style={{ minWidth: '0' }}>
                      <Text size="200" weight="medium" color="textPrimary">
                        {task.title || i18n.t('workflows.node_type.task.default.title')}
                      </Text>
                      <Text size="100" color="grey700">
                        {task.description || i18n.t('workflows.node_type.task.default.description')}
                      </Text>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
